import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useTranslation } from 'react-i18next';
import { apiKeysAPI } from '../utils/api';
import { Copy, Trash2, Eye, EyeOff, Plus, Key, Calendar, CheckCircle, XCircle } from 'lucide-react';
import '../styles/dashboard.css';

export default function APIKeys() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [apiKeys, setApiKeys] = useState([]);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [visibleKeys, setVisibleKeys] = useState({});
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchApiKeys();
  }, [isAuthenticated, navigate]);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeysAPI.list();
      setApiKeys(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    setErrorMessage('');
    try {
      const response = await apiKeysAPI.create({ name: newKeyName });
      setApiKeys([...apiKeys, response.data.data]);
      setNewKeyName('');
      setShowNewKeyModal(false);
      setNewlyCreatedKey(response.data.apiKey);
    } catch (error) {
      console.error('Failed to create API key:', error);
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      setErrorMessage(backendMsg || 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (id) => {
    if (!window.confirm(t('Are you sure you want to revoke this API key?'))) return;
    setErrorMessage('');
    try {
      await apiKeysAPI.revoke(id);
      setApiKeys(apiKeys.filter((key) => key.id !== id));
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      setErrorMessage(backendMsg || 'Failed to revoke API key');
    }
  };

  const toggleKeyVisibility = (id) => {
    setVisibleKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden">
        {/* Background Accent */}
        <div className="fixed inset-0 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-transparent pointer-events-none" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Error Message */}
          {errorMessage && (
            <div className="mb-4 sm:mb-6 relative bg-red-500/10 backdrop-blur-md border border-red-500/30 rounded-lg sm:rounded-2xl p-3 sm:p-4 text-red-400 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Page Header */}
          <div className="mb-10 sm:mb-16">
            <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 mb-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#128C7E]/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <Key className="w-5 h-5 sm:w-6 sm:h-6 text-[#128C7E]" />
                </div>
                <div style={{direction: isRTL ? 'rtl' : 'ltr'}}>
                  <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white leading-tight">{t('API Keys')}</h1>
                  <p className="text-xs sm:text-sm md:text-lg text-[#A0AEC0] mt-1 sm:mt-2">{t('Manage your API keys for integrations')}</p>
                </div>
              </div>
              <button 
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-[#128C7E] hover:bg-[#0DA699] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg shadow-[#128C7E]/20 whitespace-nowrap flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}
                onClick={() => setShowNewKeyModal(true)}
              >
                <Plus size={18} className="sm:w-5 sm:h-5" /> 
                <span className="hidden sm:inline">{t('Create New Key')}</span>
                <span className="sm:hidden">{t('New')}</span>
              </button>
            </div>
          </div>

          {/* Create Key Modal */}
          {showNewKeyModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className={`relative bg-[#1A1D23]/80 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8 w-full max-w-md shadow-2xl ${isRTL ? 'text-right' : ''}`}>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">{t('Create New API Key')}</h2>
                <input
                  type="text"
                  placeholder={t('Key name (e.g., Production, Testing)')}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#0B0E14]/50 border border-[#2D3748] rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-[#718096] focus:border-[#128C7E] focus:outline-none transition-all mb-4 sm:mb-6 ${isRTL ? 'text-right' : ''}`}
                />
                <div className={`flex flex-col sm:flex-row gap-2 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <button 
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-[#2D3748] hover:bg-[#3a4557] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all"
                    onClick={() => setShowNewKeyModal(false)}
                  >
                    {t('Cancel')}
                  </button>
                  <button 
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-[#128C7E] hover:bg-[#0DA699] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all"
                    onClick={handleCreateKey}
                  >
                    {t('Create Key')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Show newly created API key modal */}
          {newlyCreatedKey && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className={`relative bg-[#1A1D23]/80 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-8 w-full max-w-md shadow-2xl ${isRTL ? 'text-right' : ''}`}>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-4">{t('Your New API Key')}</h2>
                <p className="text-[#A0AEC0] mb-4 sm:mb-6 text-sm sm:text-base">{t("Save this key securely. You won't see it again!")}</p>
                <div className={`relative bg-[#0B0E14]/80 border border-[#2D3748] rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 overflow-auto ${isRTL ? 'text-right' : ''}`}>
                  <code className="text-[#128C7E] font-mono text-xs sm:text-sm break-all">{newlyCreatedKey}</code>
                </div>
                <button 
                  className={`w-full mb-3 sm:mb-4 flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-[#128C7E] hover:bg-[#0DA699] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                >
                  <Copy size={16} className="sm:w-5 sm:h-5" /> {t('Copy Key')}
                </button>
                <button 
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-[#2D3748] hover:bg-[#3a4557] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all"
                  onClick={() => setNewlyCreatedKey(null)}
                >
                  {t('Done')}
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <p className="text-[#A0AEC0] text-lg">{t('Loading API keys...')}</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className={`relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-8 sm:p-16 text-center ${isRTL ? 'text-right' : ''}`}>
              <Key className="w-12 h-12 sm:w-16 sm:h-16 text-[#128C7E]/30 mx-auto mb-4 sm:mb-6" />
              <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2">{t('No API Keys Yet')}</h3>
              <p className="text-xs sm:text-base text-[#A0AEC0] mb-4 sm:mb-8">{t('Create one to get started!')}</p>
              <button 
                className={`inline-flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#128C7E] hover:bg-[#0DA699] text-white font-semibold text-sm sm:text-base rounded-lg sm:rounded-xl transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                onClick={() => setShowNewKeyModal(true)}
              >
                <Plus size={18} className="sm:w-5 sm:h-5" /> {t('Create Your First Key')}
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {apiKeys.map((key) => (
                <div 
                  key={key.id}
                  className="group relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-6 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10"
                >
                <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-3 sm:mb-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-start gap-2 sm:gap-4 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-[#128C7E]/20 flex items-center justify-center flex-shrink-0">
                        <Key className="w-5 h-5 sm:w-6 sm:h-6 text-[#128C7E]" />
                      </div>
                      <div className={`min-w-0 flex-1 ${isRTL ? 'text-right' : ''}`}>
                        <h3 className="text-sm sm:text-lg font-bold text-white break-words">{key.name}</h3>
                        <p className="text-[#A0AEC0] text-xs sm:text-sm">{key.description || t('No description')}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 sm:gap-3 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className={`inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap ${
                        key.is_active 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {key.is_active ? (
                          <><CheckCircle size={12} className="sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">{t('Active')}</span><span className="sm:hidden">✓</span></>
                        ) : (
                          <><XCircle size={12} className="sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">{t('Revoked')}</span><span className="sm:hidden">✕</span></>
                        )}
                      </span>
                      {key.is_active && (
                        <button
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          onClick={() => handleRevokeKey(key.id)}
                          title={t('Revoke API Key')}
                        >
                          <Trash2 size={18} className="sm:w-5 sm:h-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Key Display */}
                    <div className="relative bg-[#0B0E14]/50 border border-[#2D3748] rounded-xl p-4">
                      <div className={`flex items-center justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <code className={`text-[#128C7E] font-mono text-sm flex-1 overflow-auto ${isRTL ? 'text-right' : ''}`}>
                          {visibleKeys[key.id] ? key.key_value : '••••••••••••••••'}
                        </code>
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <button
                            className="p-2 text-[#A0AEC0] hover:text-[#128C7E] hover:bg-[#128C7E]/10 rounded-lg transition-all"
                            onClick={() => copyToClipboard(key.key_value)}
                            title={t('Copy Key')}
                          >
                            <Copy size={18} />
                          </button>
                          <button
                            className="p-2 text-[#A0AEC0] hover:text-[#128C7E] hover:bg-[#128C7E]/10 rounded-lg transition-all"
                            onClick={() => toggleKeyVisibility(key.id)}
                            title={visibleKeys[key.id] ? t('Hide') : t('Show')}
                          >
                            {visibleKeys[key.id] ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className={`grid grid-cols-2 gap-4 text-sm ${isRTL ? 'text-right' : ''}`}>
                      <div>
                        <p className="text-[#A0AEC0] text-xs mb-1">{t('Created')}</p>
                        <div className={`flex items-center gap-2 text-white ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                          <Calendar size={14} />
                          {new Date(key.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <p className="text-[#A0AEC0] text-xs mb-1">{t('Last Used')}</p>
                        <p className="text-white">{key.last_used ? new Date(key.last_used).toLocaleDateString() : t('Never')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
